"use client";

import { OrderSummary } from "@/containers/checkout/Checkout";
import { formatDateToLongLabel } from "@/lib/utils";
import { SubscriptionResponse } from "@/services/api_types";
import { useRouter, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { sendGTMEvent } from "@next/third-parties/google";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";
import { useAppContext } from "./AppContext";
import { useAuthContext } from "./AuthContext";
import { useLayoutMode } from "@/hooks/useLayoutMode";
import { computeSurface } from "@/lib/surface";
import {
  BETALING_NIET_AFGEROND,
  BETALING_PARAM,
  TERUG_MELDING,
  bewaarBetaling
} from "@/lib/betaalRecord";
import { lifetimeCheckoutIntent, lifetimePurchaseUrl, rememberLifetimeCheckout } from "@/lib/lifetimeCheckout";
import { checkoutButtonText } from "@/lib/checkoutButtonText";
import {
  DEFAULT_CHECKOUT_PLAN_ID,
  formatPlanPriceLabel,
  getFrontendPlan,
  isSelectablePlanId
} from "@/lib/subscriptionPlans";
import { syncedCheckoutPlanId } from "@/lib/checkoutPlanSync";

type CheckoutContext = {
  orderSummary: OrderSummary;
  isProcessingCheckout: boolean;
  doCheckout: (invite_token?: string, resumeSubId?: string) => void;

  showInviteFlow: boolean;
  setShowInviteFlow: Dispatch<SetStateAction<boolean>>;

  buttonText: string;
  isResumeFlow: boolean;

  selectedPlanId: string;
  setSelectedPlanId: Dispatch<SetStateAction<string>>;

  /* wiswiz #156: één regel boven de knop wanneer de gebruiker terugkomt van een betaling die niet is afgerond */
  terugMelding: string | null;
};

const CheckoutContext = createContext<CheckoutContext | null>(null);

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);
  if (context === null) {
    throw Error("No CheckoutContext available");
  }
  return context;
};

export const CheckoutContextProvider = ({ children }: PropsWithChildren) => {
  const { api, isMobile, subscriptionPlans } = useAppContext();
  const { userData, isParent } = useAuthContext();
  const { layoutMode, platform } = useLayoutMode();
  const isMobileViewRaw = isMobile === true || layoutMode === "native";

  const router = useRouter();
  const searchParams = useSearchParams();
  const isResumeFlow = !!searchParams.get("resume");
  const defaultPlanId = DEFAULT_CHECKOUT_PLAN_ID;

  const [isProcessingCheckout, setIsProcessingCheckout] =
    useState<boolean>(false);
  /* wiswiz #156: useBetalingTerug stuurt de gebruiker hierheen met ?betaling=niet-afgerond zodra de backend zegt
     dat de betaling van net niet betaald is (terug uit Mollie zonder resultaatpagina, app herstart, terugknop). */
  const submittingRef = useRef(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const terugMelding = checkoutError ?? (
    searchParams.get(BETALING_PARAM) === BETALING_NIET_AFGEROND
      ? TERUG_MELDING
      : null);
  const [showInviteFlow, setShowInviteFlow] = useState(false);
  const queryPlanId = searchParams.get("plan_id");
  const urlPlanId = isSelectablePlanId(queryPlanId) ? queryPlanId : null;
  const [selectedPlanId, setSelectedPlanId] = useState<string>(
    urlPlanId ?? defaultPlanId
  );
  /* wiswiz #217: sync during render (the "previous value in state" pattern), so children never commit with a stale
     plan. The rule itself is in checkoutPlanSync: a changed URL plan wins, a pending click survives. */
  const [syncedUrlPlanId, setSyncedUrlPlanId] = useState<string | null>(urlPlanId);
  const syncedPlanId = syncedCheckoutPlanId({
    selectedPlanId,
    urlPlanId,
    previousUrlPlanId: syncedUrlPlanId,
    isResumeFlow
  });
  if (syncedUrlPlanId !== urlPlanId) setSyncedUrlPlanId(urlPlanId);
  if (syncedPlanId !== selectedPlanId) setSelectedPlanId(syncedPlanId);

  const selectedPlan = useMemo(
    () => getFrontendPlan(subscriptionPlans, selectedPlanId),
    [subscriptionPlans, selectedPlanId]
  );
  const priceLabel = useMemo(
    () => formatPlanPriceLabel(selectedPlan),
    [selectedPlan]
  );

  // TODO: we sure about this?
  const orderSummary: OrderSummary = useMemo(() => {
    const isResumeDuringTrial =
      isResumeFlow &&
      !!userData?.free_trial &&
      !!userData.trial_end &&
      !userData?.trial_expired;

    if (userData?.trial_expired || (isResumeFlow && !isResumeDuringTrial)) {
      return {
        title: "Jouw bestelling",
        items: [
          {
            label: selectedPlan?.isLifetime ? "WisWiz de hele schooltijd" : "WisWiz abonnement",

            price: priceLabel
          }
        ],
        trialStatus: "expired"
      };
    }

    if (userData?.free_trial && userData.trial_end) {
      const trialEndDate = new Date(userData?.trial_end);

      // Calculate days left based on actual trial end (without grace period)
      const now = new Date();
      const diffMs = trialEndDate.getTime() - now.getTime();
      const trialDaysLeft = Math.max(
        0,
        Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      );

      // Updated business logic: add grace period for subscription start date
      trialEndDate.setDate(trialEndDate.getDate() + 1);

      return {
        title: "Jouw bestelling",
        items: [
          {
            label:
              userData.role === "parent"
                ? `WisWiz abonnement`
                : `WisWiz abonnement vanaf ${formatDateToLongLabel(trialEndDate)}`,
            price: priceLabel
          }
        ],
        trialStatus: userData?.trial_expired ? "expired" : "during",
        trialDaysLeft
      };
    }

    const today = new Date();
    const sevenDaysLater = new Date(today);
    sevenDaysLater.setDate(today.getDate() + 7);
    const endLabel = formatDateToLongLabel(sevenDaysLater);

    return {
      title: "Jouw bestelling",
      items: [
        {
          label: `Proefweek tot ${endLabel}`,
          price: "€ 0,01 eenmalig"
        },
        {
          label: selectedPlan?.isLifetime ? "Eenmalige betaling na de proefweek" : "WisWiz abonnement na de proefweek",
          price: priceLabel
        }
      ],
      trialStatus: "not_started"
    };
  }, [userData, isResumeFlow, priceLabel, selectedPlan?.isLifetime]);

  const buttonText = useMemo(
    () =>
      checkoutButtonText({
        isResumeFlow,
        isParent,
        trialStatus: orderSummary.trialStatus,
        priceLabel
      }),
    [orderSummary.trialStatus, isParent, isResumeFlow, priceLabel]
  );

  const doCheckout = useCallback(
    (invite_token?: string, resumeSubId?: string) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setCheckoutError(null);

      setIsProcessingCheckout(true);
      setCheckoutError(null);

      // Fire the business action first; analytics rides alongside as a
      // passive watcher (see CLAUDE.md "Analytics is a passive watcher").
      const surface = computeSurface();
      const submittedPlanId =
        (!resumeSubId || selectedPlanId === "lifetime") && isSelectablePlanId(selectedPlanId)
          ? selectedPlanId
          : undefined;
      const checkoutPromise = (async () => {
        if (submittedPlanId === "lifetime" && !subscriptionPlans.some((plan) => plan.plan_id === "lifetime")) {
          throw new Error("Lifetime admission is closed");
        }
        const intent = submittedPlanId === "lifetime"
          ? await lifetimeCheckoutIntent(JSON.stringify([userData?.email, invite_token, resumeSubId, submittedPlanId]), platform, surface)
          : { platform, surface, requestId: undefined };
        return resumeSubId
          ? api.resumeSubscription(intent.platform, resumeSubId, intent.surface, submittedPlanId, intent.requestId)
          : api.createSubscription(intent.platform, invite_token, intent.surface, submittedPlanId, intent.requestId);
      })();
      const analyticsPlanId = resumeSubId
        ? null
        : (submittedPlanId ?? defaultPlanId);

      queueMicrotask(() => {
        try {
          posthog.register({
            [isParent
              ? "attempt_parent_buyer_session"
              : "attempt_student_buyer_session"]: true
          });
          const submittedProps = {
            user_type: isParent ? "parent" : "student",
            resume: !!resumeSubId,
            has_invite: !!invite_token,
            plan_id: analyticsPlanId
          };
          posthog.capture("checkout_submitted", submittedProps);
          posthog.capture(
            isMobileViewRaw
              ? "mobile_checkout_submitted"
              : "desktop_checkout_submitted",
            submittedProps
          );
          // Keep the legacy `start_subscription` GTM event name: Google Ads has a
          // conversion wired to it. Renaming would break ad attribution until the
          // GTM/Google Ads config is updated in lockstep.
          sendGTMEvent({ event: "start_subscription" });
        } catch (err) {
          console.warn("Checkout analytics failed", err);
        }
      });
      checkoutPromise
        .then((response: SubscriptionResponse | undefined) => {
          if (response?.checkout_url) {
            if (response.purchase_id) rememberLifetimeCheckout(response);
            else bewaarBetaling({
              id: response.payment_id ?? null, ts: Date.now(),
              terug: window.location.pathname + window.location.search,
              plan_id: analyticsPlanId ?? null
            });
            window.location.href = response.checkout_url;
          } else if (response?.purchase_id && response.subscription_id) {
            router.replace(lifetimePurchaseUrl(response.subscription_id));
          } else if (response?.subscription_start_date) {
            router.replace(isParent
              ? `/payment/gelukt?subscription_id=${response.subscription_id}&start_date=${response.subscription_start_date}`
              : `/payment/gelukt?start_date=${response.subscription_start_date}`);
          } else {
            router.replace("/");
          }
        })
        .catch(() => {
          setCheckoutError("De betaling kon niet worden gestart. Probeer het opnieuw.");
        })
        .finally(() => {
          submittingRef.current = false;
          setIsProcessingCheckout(false);
        });
    },
    [
      router,
      api,
      isProcessingCheckout,
      isParent,
      platform,
      isMobileViewRaw,
      selectedPlanId,
      isResumeFlow,
      defaultPlanId,
      subscriptionPlans,
      userData?.email
    ]
  );

  const providerValue = useMemo(
    () => ({
      orderSummary,
      doCheckout,
      isProcessingCheckout,
      showInviteFlow,
      setShowInviteFlow,
      buttonText,
      isResumeFlow,
      selectedPlanId,
      setSelectedPlanId,
      terugMelding
    }),
    [
      orderSummary,
      doCheckout,
      isProcessingCheckout,
      showInviteFlow,
      buttonText,
      isResumeFlow,
      selectedPlanId,
      terugMelding
    ]
  );

  return (
    <CheckoutContext.Provider value={providerValue}>
      {children}
    </CheckoutContext.Provider>
  );
};
